import React, { useState, useEffect } from 'react';
import { Table, Card, Spin, message, Row, Col, DatePicker, Input, Select, Space, Typography, Tag, Button, Modal, Grid } from 'antd';
import { useSelector } from 'react-redux';
import { DollarOutlined, RiseOutlined, FallOutlined, TransactionOutlined, SearchOutlined, ClockCircleOutlined, ReloadOutlined, CalendarOutlined, MailOutlined, FilterOutlined, RollbackOutlined, CheckCircleOutlined, PercentageOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import stripeTransactionsService from '../../../services/stripeTransactions';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { useBreakpoint } = Grid;

const StripeTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState({
    totalAmount: 0,
    totalTransactions: 0,
    averageAmount: 0,
    successRate: 0,
    totalReturns: 0,
    approvedReturns: 0,
    pendingReturns: 0,
    rejectedReturns: 0,
    returnRate: 0
  });
  const [filters, setFilters] = useState({
    dateRange: null,
    transactionId: '',
    status: '',
    customerEmail: ''
  });
  const { currentUser } = useSelector((state) => state.user);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const screens = useBreakpoint();

  useEffect(() => {
    if (currentUser && currentUser.role === 'admin') {
      fetchTransactions();
    }
  }, [currentUser]);

  useEffect(() => {
    applyFilters();
  }, [filters, transactions]);

  const fetchTransactions = async (forceRefresh = false) => {
    try {
      setLoading(true);
      const data = await stripeTransactionsService.getTransactions(forceRefresh);
      setTransactions(data);
      setFilteredTransactions(data);
      calculateStats(data);
      if (forceRefresh) {
        message.success('Transactions refreshed successfully');
      }
    } catch (error) {
      message.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchTransactions(true);
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    // Apply date range filter
    if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
      const startDate = filters.dateRange[0].startOf('day').unix();
      const endDate = filters.dateRange[1].endOf('day').unix();
      filtered = filtered.filter(t => 
        t.created >= startDate && t.created <= endDate
      );
    }

    // Apply transaction ID filter
    if (filters.transactionId) {
      filtered = filtered.filter(t => 
        t.id.toLowerCase().includes(filters.transactionId.toLowerCase())
      );
    }

    // Apply status filter
    if (filters.status) {
      filtered = filtered.filter(t => 
        t.status.toLowerCase() === filters.status.toLowerCase()
      );
    }

    // Apply customer email filter
    if (filters.customerEmail) {
      filtered = filtered.filter(t => 
        t.customer_email.toLowerCase().includes(filters.customerEmail.toLowerCase())
      );
    }

    setFilteredTransactions(filtered);
    calculateStats(filtered);
  };

  const calculateStats = (data) => {
    const totalAmount = data.reduce((sum, t) => sum + (t.amount / 100), 0);
    const successfulTransactions = data.filter(t => t.status === 'succeeded').length;
    const successRate = (successfulTransactions / data.length) * 100;

    // Calculate return statistics
    let totalReturns = 0;
    let approvedReturns = 0;
    let pendingReturns = 0;
    let rejectedReturns = 0;

    data.forEach(transaction => {
      transaction.lead_purchases.forEach(purchase => {
        if (purchase.lead.return_status) {
          totalReturns++;
          if (purchase.lead.return_status.status === 'approved') {
            approvedReturns++;
          } else if (purchase.lead.return_status.status === 'pending') {
            pendingReturns++;
          } else if (purchase.lead.return_status.status === 'rejected') {
            rejectedReturns++;
          }
        }
      });
    });

    const returnRate = totalReturns > 0 ? (approvedReturns / totalReturns) * 100 : 0;

    setStats({
      totalAmount,
      totalTransactions: data.length,
      averageAmount: totalAmount / data.length,
      successRate,
      totalReturns,
      approvedReturns,
      pendingReturns,
      rejectedReturns,
      returnRate
    });
  };

  const prepareChartData = () => {
    // Group transactions by date
    const groupedData = filteredTransactions.reduce((acc, transaction) => {
      const date = new Date(transaction.created * 1000).toLocaleDateString();
      if (!acc[date]) {
        acc[date] = {
          date,
          amount: 0,
          count: 0
        };
      }
      acc[date].amount += transaction.amount / 100;
      acc[date].count += 1;
      return acc;
    }, {});

    // Convert to array and sort by date
    return Object.values(groupedData).sort((a, b) => 
      new Date(a.date) - new Date(b.date)
    );
  };

  const hasTodayPurchases = (record) => {
    return record.lead_purchases.some(purchase => isToday(purchase.purchase_date));
  };

  const columns = [
    {
      title: 'Transaction ID',
      dataIndex: 'id',
      key: 'id',
      width: 150,
      render: (text, record) => (
        <Space>
          {text}
          {hasTodayPurchases(record) && (
            <Tag color="green" icon={<ClockCircleOutlined />}>
              Today's Purchase
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => `$${(amount / 100).toFixed(2)}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
    },
    {
      title: 'Customer Email',
      dataIndex: 'customer_email',
      key: 'customer_email',
    },
    {
      title: 'Date',
      dataIndex: 'created',
      key: 'created',
      render: (date) => new Date(date * 1000).toLocaleString(),
    },
    {
      title: 'Return Status',
      key: 'return_status',
      render: (_, record) => {
        const hasReturns = record.lead_purchases.some(purchase => purchase.lead.return_status);
        if (!hasReturns) return '-';
        
        const returnStatuses = record.lead_purchases
          .filter(purchase => purchase.lead.return_status)
          .map(purchase => purchase.lead.return_status.status);
        
        const uniqueStatuses = [...new Set(returnStatuses)];
        
        return (
          <Space>
            {uniqueStatuses.map((status, index) => (
              <Tag 
                key={index}
                color={status === 'approved' ? 'success' : 
                       status === 'pending' ? 'warning' : 'error'}
              >
                {status.toUpperCase()}
              </Tag>
            ))}
          </Space>
        );
      },
    },
  ];

  const isToday = (date) => {
    const today = new Date();
    const purchaseDate = new Date(date);
    return (
      purchaseDate.getDate() === today.getDate() &&
      purchaseDate.getMonth() === today.getMonth() &&
      purchaseDate.getFullYear() === today.getFullYear()
    );
  };

  const showLeadModal = (lead) => {
    console.log('Selected Lead Data:', lead);
    console.log('Return Status:', lead.return_status);
    setSelectedLead(lead);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedLead(null);
    setSelectedPurchase(null);
  };

  const expandedRowRender = (record) => {
    const leadColumns = [
      {
        title: 'Purchased By',
        dataIndex: ['user', 'name'],
        key: 'user',
        render: (_, record) =>
          record.user ? (
            <Tag color="blue" style={{ fontWeight: 500, fontSize: 13 }}>
              {`${record.user.name || ''} ${record.user.last_name || ''} (${record.user.email || ''})`}
            </Tag>
          ) : '-',
      },
      {
        title: 'Lead Name',
        dataIndex: ['lead', 'first_name'],
        key: 'name',
        render: (_, record) => (
          <Button 
            type="link" 
            onClick={() => {
              setSelectedLead(record.lead);
              setSelectedPurchase(record);
              setIsModalVisible(true);
            }}
            style={{ padding: 0 }}
          >
            {`${record.lead.first_name} ${record.lead.last_name}`}
          </Button>
        ),
      },
      {
        title: 'Email',
        dataIndex: ['lead', 'email'],
        key: 'email',
      },
      {
        title: 'Phone',
        dataIndex: ['lead', 'phone'],
        key: 'phone',
      },
      {
        title: 'State',
        dataIndex: ['lead', 'state', 'name'],
        key: 'state',
        render: (_, record) => record.lead?.state?.name || '-',
      },
      {
        title: 'Insurance Type',
        dataIndex: ['lead', 'type_of_life_insurance'],
        key: 'insurance_type',
      },
      {
        title: 'Coverage Amount',
        dataIndex: ['lead', 'coverage_amount'],
        key: 'coverage_amount',
      },
      {
        title: 'Annual Income',
        dataIndex: ['lead', 'annual_income'],
        key: 'annual_income',
      },
      {
        title: 'Lead Age',
        dataIndex: ['lead', 'lead_old'],
        key: 'lead_old',
        render: (date) => date ? new Date(date).toLocaleDateString() : '-',
      },
      {
        title: 'Purchase Date',
        dataIndex: 'purchase_date',
        key: 'purchase_date',
        render: (date) => new Date(date).toLocaleString(),
      },
      {
        title: 'Return Status',
        key: 'return_status',
        render: (_, record) => {
          if (!record.lead.return_status) return '-';
          return (
            <Tag 
              color={record.lead.return_status.status === 'approved' ? 'success' : 
                     record.lead.return_status.status === 'pending' ? 'warning' : 'error'}
            >
              {record.lead.return_status.status.toUpperCase()}
            </Tag>
          );
        },
      },
    ];

    return (
      <div style={{ padding: '16px' }}>
        <Typography.Title level={5}>Purchased Leads</Typography.Title>
        <Table
          columns={leadColumns}
          dataSource={record.lead_purchases}
          rowKey="id"
          pagination={false}
          size="small"
          onRow={(purchase) => ({
            style: {
              backgroundColor: purchase.lead.return_status ? 
                (purchase.lead.return_status.status === 'approved' ? '#f6ffed' : 
                 purchase.lead.return_status.status === 'pending' ? '#fffbe6' : 
                 '#fff1f0') : 'inherit',
              transition: 'background-color 0.3s'
            },
            onClick: () => {},
          })}
        />
      </div>
    );
  };

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="container mx-auto p-4">
        <Card>
          <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
          <p>You do not have permission to view this page.</p>
        </Card>
      </div>
    );
  }

  const chartData = prepareChartData();

  return (
    <div style={{ 
      maxWidth: '100%', 
      overflowX: 'hidden',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)',
      minHeight: '100vh',
      padding: screens.xs ? '12px' : '24px'
    }}>
      <div style={{ padding: screens.xs ? '8px' : '16px', maxWidth: '100%' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}>
              <Spin spinning={loading}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <DollarOutlined style={{ fontSize: '24px', color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#666',
                      fontWeight: 500,
                      height: 17
                    }}>
                      Total Revenue
                    </div>
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: 600,
                      color: '#389e0d',
                      lineHeight: 1.2
                    }}>
                      ${stats.totalAmount.toFixed(2)}
                    </div>
                  </div>
                </div>
              </Spin>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}>
              <Spin spinning={loading}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <TransactionOutlined style={{ fontSize: '24px', color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#666',
                      fontWeight: 500,
                      height: 17
                    }}>
                      Total Transactions
                    </div>
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: 600,
                      color: '#1890ff',
                      lineHeight: 1.2
                    }}>
                      {stats.totalTransactions}
                    </div>
                  </div>
                </div>
              </Spin>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}>
              <Spin spinning={loading}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #722ed1 0%, #531dab 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <RiseOutlined style={{ fontSize: '24px', color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#666',
                      fontWeight: 500,
                      height: 17
                    }}>
                      Average Transaction
                    </div>
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: 600,
                      color: '#722ed1',
                      lineHeight: 1.2
                    }}>
                      ${stats.averageAmount.toFixed(2)}
                    </div>
                  </div>
                </div>
              </Spin>
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}>
              <Spin spinning={loading}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FallOutlined style={{ fontSize: '24px', color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#666',
                      fontWeight: 500,
                      height: 17
                    }}>
                      Success Rate
                    </div>
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: 600,
                      color: '#52c41a',
                      lineHeight: 1.2
                    }}>
                      {stats.successRate.toFixed(2)}%
                    </div>
                  </div>
                </div>
              </Spin>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: '16px', display: 'flex', flexWrap: 'wrap' }}>
          <Col flex={1} style={{ minWidth: 200 }}>
            <Card style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}>
              <Spin spinning={loading}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #f5222d 0%, #cf1322 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <RollbackOutlined style={{ fontSize: '24px', color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#666',
                      fontWeight: 500,
                      height: 17
                    }}>
                      Total Returns
                    </div>
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: 600,
                      color: '#f5222d',
                      lineHeight: 1.2
                    }}>
                      {stats.totalReturns}
                    </div>
                  </div>
                </div>
              </Spin>
            </Card>
          </Col>
          <Col flex={1} style={{ minWidth: 200 }}>
            <Card style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}>
              <Spin spinning={loading}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #fa8c16 0%, #d46b08 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <ClockCircleOutlined style={{ fontSize: '24px', color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#666',
                      fontWeight: 500,
                      height: 17
                    }}>
                      Pending Returns
                    </div>
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: 600,
                      color: '#fa8c16',
                      lineHeight: 1.2
                    }}>
                      {stats.pendingReturns}
                    </div>
                  </div>
                </div>
              </Spin>
            </Card>
          </Col>
          <Col flex={1} style={{ minWidth: 200 }}>
            <Card style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}>
              <Spin spinning={loading}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #f5222d 0%, #cf1322 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CheckCircleOutlined style={{ fontSize: '24px', color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#666',
                      fontWeight: 500,
                      height: 17
                    }}>
                      Approved Returns
                    </div>
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: 600,
                      color: '#f5222d',
                      lineHeight: 1.2
                    }}>
                      {stats.approvedReturns}
                    </div>
                  </div>
                </div>
              </Spin>
            </Card>
          </Col>
          <Col flex={1} style={{ minWidth: 200 }}>
            <Card style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}>
              <Spin spinning={loading}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <CloseCircleOutlined style={{ fontSize: '24px', color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#666',
                      fontWeight: 500,
                      height: 17
                    }}>
                      Rejected Returns
                    </div>
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: 600,
                      color: '#52c41a',
                      lineHeight: 1.2
                    }}>
                      {stats.rejectedReturns}
                    </div>
                  </div>
                </div>
              </Spin>
            </Card>
          </Col>
          <Col flex={1} style={{ minWidth: 200 }}>
            <Card style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}>
              <Spin spinning={loading}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #f5222d 0%, #cf1322 100%)',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <PercentageOutlined style={{ fontSize: '24px', color: '#fff' }} />
                  </div>
                  <div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#666',
                      fontWeight: 500,
                      height: 17
                    }}>
                      Return Rate
                    </div>
                    <div style={{ 
                      fontSize: '24px', 
                      fontWeight: 600,
                      color: '#f5222d',
                      lineHeight: 1.2
                    }}>
                      {stats.returnRate.toFixed(2)}%
                    </div>
                  </div>
                </div>
              </Spin>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ margin: '24px 0' }}>
          <Col xs={24} lg={12}>
            <Card style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <DollarOutlined style={{ fontSize: '20px', color: '#fff' }} />
                </div>
                <Typography.Title level={5} style={{ margin: 0 }}>Daily Revenue</Typography.Title>
              </div>
              <Spin spinning={loading}>
                <div style={{ height: 300 }}>
                  {!loading && (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="amount" 
                          stroke="#1890ff" 
                          name="Revenue"
                          strokeWidth={2}
                          dot={{ r: 3 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </Spin>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
            }}>
              <div style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #52c41a 0%, #389e0d 100%)',
                  borderRadius: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <TransactionOutlined style={{ fontSize: '20px', color: '#fff' }} />
                </div>
                <Typography.Title level={5} style={{ margin: 0 }}>Daily Transactions</Typography.Title>
              </div>
              <Spin spinning={loading}>
                <div style={{ height: 300 }}>
                  {!loading && (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <Tooltip />
                        <Legend />
                        <Bar 
                          dataKey="count" 
                          fill="#52c41a" 
                          name="Transactions"
                          barSize={32}
                          radius={[8, 8, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </Spin>
            </Card>
          </Col>
        </Row>

        <Card 
          style={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
          }}
          title={
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                background: 'linear-gradient(135deg, #1890ff 0%, #096dd9 100%)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <DollarOutlined style={{ fontSize: '20px', color: '#fff' }} />
              </div>
              <Typography.Title level={5} style={{ margin: 0 }}>Transaction History</Typography.Title>
            </div>
          }
          extra={
            <Space>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: 'linear-gradient(135deg, #f6ffed 0%, #e6fffb 100%)',
                borderRadius: '8px',
                border: '1px solid #b7eb8f'
              }}>
                <TransactionOutlined style={{ 
                  fontSize: '20px',
                  color: '#52c41a',
                  background: '#f6ffed',
                  padding: '4px',
                  borderRadius: '4px'
                }} />
                <div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666',
                    fontWeight: 500,
                    height: 17
                  }}>
                    Total Transactions
                  </div>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: 600,
                    color: '#389e0d',
                    lineHeight: 1.2
                  }}>
                    {filteredTransactions.length}
                  </div>
                </div>
              </div>
              <Button
                type="primary"
                icon={<ReloadOutlined spin={refreshing} />}
                onClick={handleRefresh}
                loading={refreshing}
              >
                Refresh Data
              </Button>
            </Space>
          }
        >
          <div className="mb-4">
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={6}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <CalendarOutlined style={{ color: '#000' }} /> Date Range
                    </span>
                    <RangePicker
                      style={{ 
                        width: '100%',
                        height: 48,
                        borderRadius: 8,
                        fontSize: 16
                      }}
                      onChange={(dates) => setFilters(prev => ({ ...prev, dateRange: dates }))}
                      value={filters.dateRange}
                      disabled={loading}
                    />
                  </div>
                </Col>
                <Col xs={24} md={6}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <TransactionOutlined style={{ color: '#000' }} /> Transaction ID
                    </span>
                    <Input
                      placeholder="Search by Transaction ID"
                      prefix={<SearchOutlined style={{ color: '#000' }} />}
                      value={filters.transactionId}
                      onChange={(e) => setFilters(prev => ({ ...prev, transactionId: e.target.value }))}
                      disabled={loading}
                      style={{ 
                        height: 48,
                        borderRadius: 8,
                        fontSize: 16
                      }}
                    />
                  </div>
                </Col>
                <Col xs={24} md={6}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <MailOutlined style={{ color: '#000' }} /> Customer Email
                    </span>
                    <Input
                      placeholder="Search by Customer Email"
                      prefix={<SearchOutlined style={{ color: '#000' }} />}
                      value={filters.customerEmail}
                      onChange={(e) => setFilters(prev => ({ ...prev, customerEmail: e.target.value }))}
                      disabled={loading}
                      style={{ 
                        height: 48,
                        borderRadius: 8,
                        fontSize: 16
                      }}
                    />
                  </div>
                </Col>
                <Col xs={24} md={6}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
                      <FilterOutlined style={{ color: '#000' }} /> Status
                    </span>
                    <Select
                      style={{ 
                        width: '100%',
                        height: 48,
                        borderRadius: 8,
                        fontSize: 16
                      }}
                      placeholder="Filter by Status"
                      value={filters.status}
                      onChange={(value) => setFilters(prev => ({ ...prev, status: value }))}
                      allowClear
                      disabled={loading}
                      suffixIcon={<FilterOutlined style={{ color: '#000' }} />}
                    >
                      <Option value="succeeded">Succeeded</Option>
                      <Option value="failed">Failed</Option>
                      <Option value="pending">Pending</Option>
                    </Select>
                  </div>
                </Col>
              </Row>
            </Space>
          </div>

          <Spin spinning={loading}>
            <Table
              dataSource={filteredTransactions}
              columns={columns}
              rowKey="id"
              pagination={{ pageSize: 10 }}
              scroll={{ x: 'max-content' }}
              expandable={{
                expandedRowRender,
                rowExpandable: (record) => record.lead_purchases && record.lead_purchases.length > 0,
              }}
            />
          </Spin>
        </Card>
      </div>

      <Modal
        title={<span style={{ fontWeight: 600, fontSize: 20 }}>Lead Details</span>}
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={null}
        width={800}
        bodyStyle={{ padding: 24 }}
        style={{ borderRadius: 12 }}
      >
        {selectedPurchase && (
          <div style={{
            display: 'flex',
            gap: 8,
            marginBottom: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}>
            <span style={{
              background: '#e6fffb',
              color: '#08979c',
              borderRadius: 12,
              padding: '2px 12px',
              fontWeight: 600,
              fontSize: 13,
              display: 'inline-block',
            }}>
              Transaction ID: <span style={{ fontWeight: 700 }}>{selectedPurchase.transaction_id}</span>
            </span>
            <span style={{
              background: '#f6ffed',
              color: '#389e0d',
              borderRadius: 12,
              padding: '2px 12px',
              fontWeight: 600,
              fontSize: 13,
              display: 'inline-block',
            }}>
              Purchase Date: <span style={{ fontWeight: 700 }}>{new Date(selectedPurchase.purchase_date).toLocaleDateString()}</span>
            </span>
          </div>
        )}
        {selectedLead && (
          <div style={{ padding: '16px' }}>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card 
                  title={<span style={{ fontWeight: 500, fontSize: 16 }}>Return Information</span>}
                  bordered={false}
                  style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                >
                  {selectedLead && selectedLead.return_status ? (
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>Return Date</Typography.Text>
                        <Typography.Text>
                          {selectedLead.return_status.created_at ? new Date(selectedLead.return_status.created_at).toLocaleDateString() : 'No return date'}
                        </Typography.Text>
                      </Col>
                      <Col span={12}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>Return Status</Typography.Text>
                        <Typography.Text>
                          <Tag color={selectedLead.return_status.status === 'approved' ? 'success' : 
                                     selectedLead.return_status.status === 'pending' ? 'warning' : 'error'}>
                            {selectedLead.return_status.status.toUpperCase()}
                          </Tag>
                        </Typography.Text>
                      </Col>
                      <Col span={24}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>Return Reason</Typography.Text>
                        <Typography.Text>
                          {selectedLead.return_status.reason || 'No return reason provided'}
                        </Typography.Text>
                      </Col>
                      <Col span={24}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>Last Updated</Typography.Text>
                        <Typography.Text>
                          {selectedLead.return_status.updated_at ? new Date(selectedLead.return_status.updated_at).toLocaleString() : 'No update date'}
                        </Typography.Text>
                      </Col>
                    </Row>
                  ) : (
                    <Typography.Text type="secondary">No return information available</Typography.Text>
                  )}
                </Card>
              </Col>
              <Col span={24}>
                <Card 
                  title={<span style={{ fontWeight: 500, fontSize: 16 }}>All Details</span>}
                  bordered={false}
                  style={{ borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}
                >
                  <Row gutter={[24, 16]}>
                    <Col span={12}>
                      <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>First Name</Typography.Text>
                        <Typography.Text>{selectedLead.first_name || '-'}</Typography.Text>
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>Last Name</Typography.Text>
                        <Typography.Text>{selectedLead.last_name || '-'}</Typography.Text>
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>Email</Typography.Text>
                        <Typography.Text>{selectedLead.email || '-'}</Typography.Text>
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>Phone</Typography.Text>
                        <Typography.Text>{selectedLead.phone || '-'}</Typography.Text>
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>State</Typography.Text>
                        <Typography.Text>
                          {selectedLead.state ? (
                            <span>
                              <span style={{ fontWeight: 500 }}>{selectedLead.state.name}</span>
                              <span style={{ marginLeft: 8, color: '#666' }}>({selectedLead.state.short_form})</span>
                            </span>
                          ) : '-'}
                        </Typography.Text>
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>Lead Old</Typography.Text>
                        <Typography.Text>{selectedLead.lead_old ? new Date(selectedLead.lead_old).toLocaleDateString() : '-'}</Typography.Text>
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>Type Of Life Insurance</Typography.Text>
                        <Typography.Text>{selectedLead.type_of_life_insurance || '-'}</Typography.Text>
                      </div>
                    </Col>
                    <Col span={12}>
                      <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>Coverage Amount</Typography.Text>
                        <Typography.Text>{selectedLead.coverage_amount || '-'}</Typography.Text>
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>Final Expense Coverage</Typography.Text>
                        <Typography.Text>{selectedLead.final_expense_coverage || '-'}</Typography.Text>
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>Beneficiary</Typography.Text>
                        <Typography.Text>{selectedLead.beneficiary || '-'}</Typography.Text>
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>Annual Income</Typography.Text>
                        <Typography.Text>{selectedLead.annual_income || '-'}</Typography.Text>
                      </div>
                      <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>Mortgage Protection Amount</Typography.Text>
                        <Typography.Text>{selectedLead.mortgage_protection_amount || '-'}</Typography.Text>
                      </div>
                      {/* <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>Funeral Type</Typography.Text>
                        <Typography.Text>{selectedLead.funeral_type || '-'}</Typography.Text>
                      </div> */}
                      {/* <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong style={{ display: 'block', marginBottom: 4 }}>Mortgage Amount Owed</Typography.Text>
                        <Typography.Text>{selectedLead.mortgage_amount_owed || '-'}</Typography.Text>
                      </div> */}
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StripeTransactions; 